import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import Grid from '@material-ui/core/Grid';
import { CheckboxField } from 'components/FormFields';
import { VehiclesSelect, VehiclesGroupsSelect } from 'components/AsyncFields';
import { ReservationsActions } from 'state/modules/reservations';
import { UserContext } from 'context';

// Validation schema of update user form
const UpdateReservationSchema = Yup.object().shape({
  vehicle_id: Yup.number().required(i18n.t('common.required_field')),
});

const ChangeVehicleReservationModal = ({
  id,
  open,
  vehicleId,
  handleClick,
  vehicleGroup,
  reservationUserId,
}) => {
  const { orgType } = useContext(UserContext);
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchedReservationSuccess, fetchedReservationError, reservationLoading } = useSelector(
    (state) => state.reservations
  );
  const { fetchingVehicles } = useSelector((state) => state.vehicles);

  // Initials values of the form
  const initialValues = {
    vehicle_id: vehicleId || '',
    vehicle_group_id: vehicleGroup?.id || '',
    options: { bypass_rules: false },
  };

  useEffect(() => {
    if (fetchedReservationSuccess) {
      handleClick();
      dispatch(ReservationsActions.resetUpdateReservation());
    }
  }, [fetchedReservationSuccess, handleClick, dispatch]);

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = (values) => {
    if (id) {
      const payload = { ...values };
      payload.action = 'CHANGEVEHICLE';
      return dispatch(ReservationsActions.updateReservation(id, payload));
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      maxWidth={480}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={reservationLoading}
      title={i18n.t('dashboard.noc_change_vehicle')}
      enterButtonLabel={i18n.t('dashboard.noc_change_vehicle')}
      privileges={{ action: 'update_vehicle', subject: 'reservation' }}
      data-cy="change-vehicle-modal"
    >
      <Grid
        container
        direction="column"
        data-cy="change-reservation-vehicle-modal"
        alignItems="flex-start"
      >
        <Box width="100%">
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={UpdateReservationSchema}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        margin="none"
                        id="vehicleGroupSelect"
                        name="vehicle_group_id"
                        timezoneField="timezone"
                        user_id={reservationUserId}
                        disabled={!reservationUserId}
                        component={VehiclesGroupsSelect}
                        defaultVehicleGroup={vehicleGroup}
                        label={i18n.t(
                          ['msServiceOrg', 'plainOrg'].includes(orgType)
                            ? 'vehicle.vehicle_group'
                            : 'vehicle.service_area'
                        )}
                        onChange={() => {
                          setFieldValue('vehicle_id', '');
                        }}
                        customParams={{
                          group_type: orgType === 'msOwnerOrg' ? 'OWNER' : 'CUSTOMER',
                          include_children: true,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        name="vehicle_id"
                        id="reservation_vehicle_select"
                        component={VehiclesSelect}
                        label={i18n.t('common.vehicle')}
                        disabled={!values?.vehicle_group_id || fetchingVehicles === 'LOADING'}
                        vehicle_group_id={values?.vehicle_group_id}
                        customParams={{
                          exclude_unshared: true,
                          skip_buffer: values?.options?.bypass_rules,
                        }}
                        customNoOptionText={i18n.t('reservations.no_vehicles_by_date')}
                      />
                    </Box>
                  </Grid>
                  {fetchedReservationError && (
                    <Grid item xs={12}>
                      <Box>
                        <Field
                          name="bypass"
                          labelPlacement="end"
                          field={{
                            name: 'options.bypass_rules',
                            value: values?.options?.bypass_rules,
                          }}
                          component={CheckboxField}
                          label={i18n.t('reservations.bypass')}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

ChangeVehicleReservationModal.propTypes = {
  id: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
  reservationUserId: PropTypes.number.isRequired,
  vehicleId: PropTypes.number,
  vehicleGroup: PropTypes.object,
  handleClick: PropTypes.func.isRequired,
};

ChangeVehicleReservationModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangeVehicleReservationModal;
